import { useFragment } from "react-relay"
import { convertLanguageToISOCode } from "@/lib/language-to-iso-code"
import { ChatMessageTimeAgo } from "@/modules/chats/components/chat-message/chat-message-time-ago"
import { ChatThreadUnreadMessage } from "@/modules/chats/components/chat-thread/chat-thread-unread-message"
import { ChatThreadItemFragment$key } from "@/modules/chats/services/fragments/__generated__/ChatThreadItemFragment.graphql"
import { ChatThreadItemFragment } from "@/modules/chats/services/fragments/ChatThreadItemFragment"
import { ChatThread } from "@/modules/chats/types/chat-thread"

type ChatThreadItemProps = {
  chatThread: ChatThread
  appointmentRef: ChatThreadItemFragment$key
  selected?: boolean
}

const LANGUAGE_BADGE_STYLES_MAPPING: Record<string, string> = {
  EN: "bg-pink-surface-subdued text-pink",
  DE: "bg-lilac-surface-subdued text-lilac",
  FR: "bg-ochre-surface-subdued text-ochre",
  IT: "bg-plum-surface-subdued text-plum",
}

const LanguageBadge = ({ shortLanguage }: { shortLanguage: string }) => {
  const badgeStyles = LANGUAGE_BADGE_STYLES_MAPPING[shortLanguage]
  return badgeStyles ? (
    <div
      data-testid="chat-thread-language"
      className={`flex h-4 w-7.5 shrink-0 items-center justify-center rounded-full text-center text-xs ${badgeStyles}`}>
      {shortLanguage}
    </div>
  ) : null
}

export const ChatThreadItem = (props: ChatThreadItemProps) => {
  const { chatThread, appointmentRef, selected } = props

  const data = useFragment<ChatThreadItemFragment$key>(ChatThreadItemFragment, appointmentRef)
  const lastMessageCreatedOn = data.chat?.lastMessage.createdOn

  return (
    <>
      <div className="line-clamp-1 flex justify-center text-xs @md:hidden">
        <div
          className={`mt-1 flex flex-row content-end items-center gap-0.5 truncate ${selected ? "text-on-primary-surface" : "text-on-surface"}`}>
          <LanguageBadge shortLanguage={convertLanguageToISOCode(chatThread.language)} />
          <p>{`${chatThread.patient.age.years} y`}</p>
        </div>
      </div>
      <div
        data-testid={`chat-thread-item-${chatThread.id}`}
        className={`hidden w-full grid-rows-[20px_auto] @md:grid ${selected ? "text-on-primary-surface" : "text-on-surface"}`}>
        <div className="flex items-center justify-between gap-2 text-sm">
          <LanguageBadge shortLanguage={convertLanguageToISOCode(chatThread.language)} />
          <div className="flex w-full max-w-45 flex-row items-center justify-between gap-2">
            <div className="truncate" data-testid="chat-thread-patient-name">
              {chatThread.patient.firstName} {chatThread.patient.lastName}
            </div>
            {lastMessageCreatedOn && <ChatMessageTimeAgo sendMessageDate={new Date(lastMessageCreatedOn)} />}
          </div>
        </div>
        <ChatThreadUnreadMessage appointmentRef={appointmentRef} />
      </div>
    </>
  )
}
